import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permissionLevelEnum } from '../../../../constants';
import { useUpdateSystemConfigMutation } from '../../../../hooks/mutations/useSystemConfigMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { SystemConfig } from '../../../../services/apiSLTP/systemConfig';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import UpdateSystemConfigModal from '../../../modals/admin/configuration/UpdateSystemConfigModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface SystemConfigsSettingsTableProps {}

const SystemConfigsSettingsTable: React.FC<SystemConfigsSettingsTableProps> = ({}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { configs, refetch } = useSystemConfig();

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<any | null>(null);

    const { mutate } = useUpdateSystemConfigMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating config:', error.message),
    });

    const columnDefs = useMemo(
        () =>
            [
                {
                    headerName: capitalizeFirstLetter(t('key')),
                    field: 'key',
                    initialFlex: 1,
                    minWidth: 200,
                    colId: 'key',
                },
                {
                    headerName: capitalizeFirstLetter(t('actions')),
                    cellRenderer: (params: ICellRendererParams) => {
                        if (!params.data) return null;
                        return (
                            <IconLine>
                                {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                                    <IconButton
                                        iconName="edit"
                                        title={capitalizeFirstLetter(t('modify'))}
                                        onClick={() => {
                                            setSelectedConfig(params.data);
                                            setShowEditModal(true);
                                        }}
                                    />
                                )}
                            </IconLine>
                        );
                    },
                    sortable: false,
                    filter: false,
                    colId: 'actions',
                },
                {
                    headerName: capitalizeFirstLetter(t('value')),
                    field: 'value',
                    cellRendererSelector: (params) => {
                        if (params.value === null || params.value === undefined) return undefined;
                        if (typeof params.value === 'boolean') {
                            return { component: 'agCheckboxCellRenderer' };
                        }
                        return { component: (props: any) => <div>{props.value.toString()}</div> };
                    },
                    initialWidth: 300,
                    colId: 'value',
                },
                {
                    headerName: capitalizeFirstLetter(t('description')),
                    field: 'description',
                    initialWidth: 400,
                    colId: 'description',
                },
                {
                    headerName: capitalizeFirstLetter(t('group')),
                    field: 'group',
                    colId: 'group',
                },
            ] as ColDef<SystemConfig>[],
        [t, user]
    );

    return (
        <>
            <GenericAgGridTable
                rowData={configs?.sort((a, b) => a.key.localeCompare(b.key)) || []}
                columnDefs={columnDefs}
                loading={!configs}
                exportCsvFileName="admin-system-configs"
                agGridProps={{
                    getRowId: ({ data }) => data._id,
                }}
            />

            {showEditModal && (
                <UpdateSystemConfigModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    config={selectedConfig}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default SystemConfigsSettingsTable;
